import { useCallback, useState } from 'react';
import { api } from '../../services/api';
import { CompatibilityProfile, ConnectionRequest, MatchItem } from './types';

export const useCompatibility = (initialHasProfile?: boolean) => {
  const [loading, setLoading] = useState<boolean>(false);
  // Seeded from the caller's already-known state (e.g. AuthContext's cached
  // User.hasCompatibilityProfile) when available, so pages the sidebar routed
  // to with high confidence don't flash a "checking..."/wrong-state view
  // while a redundant profile-check request is still in flight.
  const [hasProfile, setHasProfile] = useState<boolean>(initialHasProfile ?? false);
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [history, setHistory] = useState<ConnectionRequest[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<Partial<CompatibilityProfile>>({});

  const checkUserProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backend/compatibility/profile/check');
      setHasProfile(res.data.hasProfile);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  // Persists every page's data immediately (not just the final page) so
  // progress survives a refresh mid-form — the backend upserts on each call.
  // Only the final page sends isProfileComplete: true, so a half-filled form
  // is never mistaken for a completed one.
  const saveFormData = useCallback(
    async (data: Partial<CompatibilityProfile>, finalStep = false) => {
      setLoading(true);
      try {
        const mergedData = { ...formData, ...data };
        setFormData(mergedData);

        const res = await api.post('/backend/compatibility/profile', {
          ...data,
          ...(finalStep ? { isProfileComplete: true } : {}),
        });
        if (finalStep) {
          setHasProfile(true);
        }
        return res.data;
      } finally {
        setLoading(false);
      }
    },
    [formData]
  );

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backend/compatibility/matches');
      setMatches(res.data.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendConnectionRequest = useCallback(async (toUserId: string, message?: string) => {
    setLoading(true);
    try {
      const res = await api.post('/backend/compatibility/request', { toUserId, message });
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptConnectionRequest = useCallback(async (requestId: string) => {
    setLoading(true);
    try {
      const res = await api.put(`/backend/compatibility/request/${requestId}/accept`);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const rejectConnectionRequest = useCallback(async (requestId: string) => {
    setLoading(true);
    try {
      const res = await api.put(`/backend/compatibility/request/${requestId}/reject`);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (status?: string) => {
    setLoading(true);
    try {
      const res = await api.get('/backend/compatibility/requests/history', { params: { status } });
      setHistory(res.data.data);
      return res.data;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSynastryReport = useCallback(async (requestId: string) => {
    const res = await api.get(`/backend/compatibility/request/${requestId}/synastry`);
    return res.data;
  }, []);

  return {
    loading,
    hasProfile,
    matches,
    history,
    currentStep,
    setCurrentStep,
    formData,
    setFormData,
    checkUserProfile,
    saveFormData,
    fetchMatches,
    sendConnectionRequest,
    acceptConnectionRequest,
    rejectConnectionRequest,
    fetchHistory,
    fetchSynastryReport,
  };
};
